import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, ChevronDown, Globe } from 'lucide-react';
import { cn } from '@/src/lib/utils';
import { useCurrency, CurrencyCode } from '@/src/contexts/CurrencyContext';
import { useUnits, UnitSystem } from '@/src/contexts/UnitContext';
import GlobalSearch from '../common/GlobalSearch';

interface NavbarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  onMenuClick?: () => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

export default function Navbar({ onMenuClick, searchQuery, setSearchQuery }: NavbarProps) {
  const navigate = useNavigate();
  const { currency, setCurrency, currencies } = useCurrency();
  const { unitSystem, setUnitSystem } = useUnits();
  
  const [isCurrencyOpen, setIsCurrencyOpen] = useState(false);
  const [isUnitsOpen, setIsUnitsOpen] = useState(false);
  
  const currencyDropdownRef = useRef<HTMLDivElement>(null);
  const unitsDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(event.target as Node)) {
        setIsCurrencyOpen(false);
      }
      if (unitsDropdownRef.current && !unitsDropdownRef.current.contains(event.target as Node)) {
        setIsUnitsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const UNIT_OPTIONS: { value: UnitSystem; label: string; suffix: string }[] = [
    { value: 'METRIC', label: 'Metric', suffix: 'kg/cm' },
    { value: 'IMPERIAL', label: 'Imperial', suffix: 'lb/in' },
    { value: 'BRITISH', label: 'British', suffix: 'st/in' }
  ];

  const currentUnitOption = UNIT_OPTIONS.find(o => o.value === unitSystem) || UNIT_OPTIONS[0];

  return (
    <nav className="bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-[100] w-full flex flex-col">
      {/* ROW 1: MINIMAL TOP HEADER NAVIGATION */}
      <div className="h-[64px] flex items-center justify-between px-4 md:px-8 w-full max-w-7xl mx-auto">
        {/* Left Side: Burger Menu Trigger followed immediately by branding */}
        <div className="flex items-center gap-3">
          <button 
            onClick={onMenuClick}
            className="p-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900 rounded-xl text-slate-600 dark:text-slate-300 transition-colors flex items-center justify-center shrink-0"
            aria-label="Open sidebar"
          >
            <Menu size={22} className="stroke-[2.5]" />
          </button>

          <div className="flex flex-col cursor-pointer shrink-0" onClick={() => navigate('/')}>
            <span className="text-xl md:text-2xl font-black tracking-tighter leading-none">
              <span className="text-emerald-800 dark:text-emerald-400">META</span><span className="text-black dark:text-purple-400">.UTILIX</span>
            </span>
            <span className="hidden sm:inline text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mt-0.5">
              Advanced Utility Protocol
            </span>
          </div>
        </div>

        {/* Middle: Global Search */}
        <div className="flex-1 px-8 hidden md:flex justify-center">
            <GlobalSearch searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
        </div>

        {/* Right Side: Standalone [ PREMIUM ] button */}
        <div className="flex items-center">
        </div>
      </div>

      {/* ROW 2: NEW DEDICATED "GLOBAL CONFIGURATION BAR" */}
      <div className="bg-slate-50 dark:bg-slate-900/50 border-t border-slate-150 dark:border-slate-900/80 h-[50px] px-2 md:px-8 w-full flex items-center">
        <div className="max-w-xl sm:max-w-2xl mx-auto grid grid-cols-2 gap-1.5 sm:gap-4 w-full text-sans items-center">
          
          {/* Measurement System Selector Dropdown */}
          <div className="relative w-full" ref={unitsDropdownRef}>
            <button 
              onClick={() => setIsUnitsOpen(!isUnitsOpen)}
              className="flex items-center justify-center gap-1 px-1.5 sm:px-3 py-1.5 cursor-pointer bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-800 rounded-xl hover:border-brand/30 dark:hover:border-brand/30 transition-all text-slate-700 dark:text-slate-300 h-9 w-full"
            >
              <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider truncate">
                {currentUnitOption.label} ({currentUnitOption.suffix})
              </span>
              <ChevronDown size={10} className={cn("text-slate-400 transition-transform duration-300 shrink-0", isUnitsOpen && "rotate-180")} />
            </button>

            {isUnitsOpen && (
              <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-40 sm:w-48 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl p-2 z-[110] animate-in fade-in zoom-in-95 duration-200">
                <div className="grid grid-cols-1 gap-0.5">
                  {UNIT_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        setUnitSystem(opt.value);
                        setIsUnitsOpen(false);
                      }}
                      className={cn(
                        "flex items-center justify-between w-full px-3 py-1.5 rounded-xl transition-all cursor-pointer text-left",
                        unitSystem === opt.value 
                          ? "bg-brand/5 dark:bg-brand/10 text-brand" 
                          : "hover:bg-slate-50 dark:hover:bg-slate-900 text-slate-700 dark:text-slate-300"
                      )}
                    >
                      <div className="flex flex-col">
                        <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider">{opt.label}</span>
                        <span className="text-[8px] font-bold text-slate-400 dark:text-slate-500 uppercase">{opt.suffix}</span>
                      </div>
                      {unitSystem === opt.value && (
                        <div className="w-1.5 h-1.5 rounded-full bg-brand" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Currency Selector Dropdown */}
          <div className="relative w-full" ref={currencyDropdownRef}>
            <button 
              onClick={() => setIsCurrencyOpen(!isCurrencyOpen)}
              className="flex items-center justify-center gap-1 px-1.5 sm:px-3 py-1.5 cursor-pointer bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-800 rounded-xl hover:border-brand/30 dark:hover:border-brand/30 transition-all text-slate-700 dark:text-slate-300 h-9 w-full"
            >
              <span className="text-xs sm:text-sm shrink-0">{currency.flag}</span>
              <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider truncate">
                {currency.code} ({currency.symbol})
              </span>
              <ChevronDown size={10} className={cn("text-slate-400 transition-transform duration-300 shrink-0", isCurrencyOpen && "rotate-180")} />
            </button>

            {isCurrencyOpen && (
              <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-48 sm:w-56 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl p-2 z-[110] animate-in fade-in zoom-in-95 duration-200">
                <div className="px-3 py-1.5 mb-1 border-b border-slate-100 dark:border-slate-900">
                  <div className="flex items-center gap-2 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                    <Globe size={11} />
                    Region & Currency
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-0.5 max-h-[220px] overflow-y-auto custom-scrollbar pr-0.5">
                  {currencies.map((c) => (
                    <button
                      key={c.code}
                      onClick={() => {
                        setCurrency(c.code as CurrencyCode);
                        setIsCurrencyOpen(false);
                      }}
                      className={cn(
                        "flex items-center justify-between w-full px-3 py-1.5 rounded-xl transition-all cursor-pointer",
                        currency.code === c.code 
                          ? "bg-brand/5 dark:bg-brand/10" 
                          : "hover:bg-slate-50 dark:hover:bg-slate-900"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-base">{c.flag}</span>
                        <div className="flex flex-col items-start">
                          <span className={cn("text-[9px] sm:text-[10px] font-black uppercase tracking-wider", currency.code === c.code ? "text-brand" : "text-slate-900 dark:text-slate-100")}>
                            {c.code}
                          </span>
                          <span className="text-[8px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-tight">
                            {c.symbol}
                          </span>
                        </div>
                      </div>
                      {currency.code === c.code && (
                        <div className="w-1.5 h-1.5 rounded-full bg-brand" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </nav>
  );
}
